import React, { useEffect, useRef, useState } from 'react';
import {
  HERO_VIDEO_MP4,
  HERO_VIDEO_YOUTUBE_ID,
  HERO_VIDEO_LOOP_AT,
  HERO_POSTER,
} from '../config';
import { foto } from '../imagenes';

/**
 * Fondo de video del hero.
 *
 * El poster pinta de inmediato (es el LCP) y el reproductor se monta
 * después del evento load, en reposo, para no competir con la página.
 *
 * Con YouTube se usa la IFrame API en lugar de un iframe suelto porque
 * es la única forma de rebobinar antes del final: los últimos segundos
 * del video no funcionan como fondo. El script se carga diferido.
 */
const HeroVideo: React.FC = () => {
  const [mountPlayer, setMountPlayer] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<{ destroy?: () => void } | null>(null);
  const containerId = 'hero-video-player';

  // Monta el reproductor después del primer pintado.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } })
      .connection;
    if (conn?.saveData || (conn?.effectiveType && /2g/.test(conn.effectiveType))) return;

    /* Se espera al evento load y luego a que el navegador quede libre: el
       reproductor de YouTube son ~2,7 MB (video + scripts) y, arrancado
       antes, competía con la propia página en celular. El poster cubre
       mientras tanto. */
    let cancelado = false;
    let t = 0;
    const start = () => {
      if (!cancelado) setMountPlayer(true);
    };
    const enReposo = () => {
      const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
        .requestIdleCallback;
      if (idle) idle(start, { timeout: 3000 });
      else t = window.setTimeout(start, 900);
    };
    if (document.readyState === 'complete') enReposo();
    else window.addEventListener('load', enReposo, { once: true });
    return () => {
      cancelado = true;
      window.clearTimeout(t);
      window.removeEventListener('load', enReposo);
    };
  }, []);

  // Crea el reproductor de YouTube una vez montado.
  useEffect(() => {
    if (!mountPlayer || HERO_VIDEO_MP4) return;

    type YTWindow = Window & {
      YT?: { Player: new (id: string, opts: unknown) => unknown };
      onYouTubeIframeAPIReady?: () => void;
    };
    const w = window as YTWindow;
    let loopTimer: number | undefined;

    const createPlayer = () => {
      if (!w.YT) return;
      playerRef.current = new w.YT.Player(containerId, {
        videoId: HERO_VIDEO_YOUTUBE_ID,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          rel: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          playsinline: 1,
          enablejsapi: 1,
          disablekb: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: (e: { target: { playVideo: () => void; seekTo: (s: number) => void; getCurrentTime: () => number; setPlaybackQuality: (q: string) => void } }) => {
            /* 1080p solo donde se nota; en celular YouTube elige la calidad
               según la conexión (antes se forzaba 1080p también ahí). */
            if (window.innerWidth >= 1024) e.target.setPlaybackQuality('hd1080');
            e.target.playVideo();
            setIsPlaying(true);
            // Rebobina antes del cierre del video.
            loopTimer = window.setInterval(() => {
              if (e.target.getCurrentTime() >= HERO_VIDEO_LOOP_AT) e.target.seekTo(0);
            }, 200);
          },
          onStateChange: (e: { data: number; target: { seekTo: (s: number) => void; playVideo: () => void } }) => {
            if (e.data === 0) {
              e.target.seekTo(0);
              e.target.playVideo();
            }
          },
        },
      }) as { destroy?: () => void };
    };

    if (w.YT?.Player) {
      createPlayer();
    } else {
      w.onYouTubeIframeAPIReady = createPlayer;
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    }

    return () => {
      if (loopTimer) window.clearInterval(loopTimer);
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, [mountPlayer]);

  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden bg-black" aria-hidden="true">
      {/* Poster: lo primero que se ve y lo que mide el LCP */}
      <img
        {...foto(HERO_POSTER, '100vw')}
        alt=""
        fetchPriority="high"
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
          isPlaying ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {mountPlayer && (
        <div className={`absolute inset-0 transition-opacity duration-1000 ${isPlaying ? 'opacity-100' : 'opacity-0'}`}>
          {HERO_VIDEO_MP4 ? (
            <video
              src={HERO_VIDEO_MP4}
              poster={HERO_POSTER}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onPlaying={() => setIsPlaying(true)}
              className="h-full w-full object-cover"
            />
          ) : (
            /* Sobredimensionado para tapar las barras negras del 16:9 */
            <div
              id={containerId}
              className="absolute"
              style={{ width: '150%', height: '150%', top: '-25%', left: '-25%' }}
            />
          )}
        </div>
      )}

      {/* Velado cinematográfico: deja legible el texto sobre cualquier fotograma */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/30" />
    </div>
  );
};

export default HeroVideo;
